"use client";

import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { SectionContainer } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { TextGradient } from "@/components/ui/text-gradient";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  imageSrc: string;
  socialLinks?: {
    type: string;
    url: string;
  }[];
}

export function TeamSection() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const teamMembers: TeamMember[] = [
    {
      name: "Emma Blueford",
      role: "Executive Director",
      bio: "Emma founded Blue Legos Matter after noticing the systematic underrepresentation of blue bricks in her childhood collection. With 15+ years in non-profit leadership, she's passionate about brick equality.",
      imageSrc: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80",
    },
    {
      name: "Marcus Chen",
      role: "Creative Director",
      bio: "A master builder with a background in design, Marcus oversees all creative initiatives. His award-winning blue-centric builds have been featured in exhibitions worldwide.",
      imageSrc: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80",
    },
    {
      name: "Sophia Navarro",
      role: "Education Program Lead",
      bio: "With a Ph.D. in Early Childhood Development, Sophia designs curricula that teach children about the importance of color diversity in creative play and construction.",
      imageSrc: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80",
    },
    {
      name: "Jordan Reynolds",
      role: "Blue Brick Rescue Coordinator",
      bio: "Jordan manages our nationwide network of brick collection and redistribution sites. Their logistics expertise ensures no blue brick is left behind or forgotten.",
      imageSrc: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80",
    },
  ];

  return (
    <SectionContainer id="team" className="bg-gray-50">
      <SectionHeading
        title={
          <>
            Meet Our <TextGradient text="Team" />
          </>
        }
        subtitle="Our dedicated team of blue brick advocates works tirelessly to promote equality and appreciation for blue Lego pieces around the world."
      />

      <div 
        ref={ref} 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
      >
        {teamMembers.map((member, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          >
            <Card className="overflow-hidden h-full group">
              <div className="relative">
                <div className="aspect-[3/4] overflow-hidden">
                  <img 
                    src={member.imageSrc} 
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className={cn(
                  "absolute inset-0 bg-gradient-to-t from-black/60 to-transparent",
                  "transition-opacity duration-300 opacity-50 group-hover:opacity-70"
                )} />
              </div>
              <div className="p-6 relative">
                <div className="absolute -top-16 left-0 right-0 p-4">
                  <h3 className="text-xl font-bold text-white">{member.name}</h3>
                  <p className="text-blue-100 font-medium">{member.role}</p>
                </div>
                <div className="pt-6">
                  <p className="text-muted-foreground text-sm">{member.bio}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-12 text-center"
      >
        <p className="text-muted-foreground">
          Interested in joining our team? We're always looking for passionate blue brick advocates!
        </p>
        <a 
          href="#careers" 
          className="inline-flex items-center text-blue-medium hover:text-blue-dark font-medium mt-2"
        >
          View Open Positions 
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 ml-1">
            <path fillRule="evenodd" d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z" clipRule="evenodd" />
          </svg>
        </a>
      </motion.div>
    </SectionContainer>
  );
}